import umlCanvasEngine from '../../Core/UmlCanvasEngine';
import umlConnectorEngine from '../../Core/UmlConnectorEngine';
import { getImportStrategy } from '../../Core/Import/ImportStrategyRegistry';
import { findCanvasConnectors } from '../../Core/Import/CanvasConnectorFinder';
import { nextPosition } from './UmlSchemaFileImportHelpers';

/**
 * Encapsulates the file-import "Generate" flow: runs every PENDING entry's
 * matched strategy one file at a time (in order, so later files see earlier
 * ones' shapes/connectors as existing), then does a retroactive full-canvas
 * connector pass. Mutates umlCanvasEngine/umlConnectorEngine directly, kept
 * out of the component per the "no logic injection" pattern (rk=e265293c).
 * @param {object} params
 * @param {object[]} params.droppedFiles
 * @param {(updater: (current: object[]) => object[]) => void} params.setDroppedFiles
 * @param {(warnings: string[]) => void} params.setLastWarnings
 * @param {(value: boolean) => void} params.setIsGenerating
 * @param {boolean} params.collapsed - build every imported class shape already collapsed
 * @returns {{handleGenerate: () => Promise<void>}}
 */
export default function useFileImportGenerate({ droppedFiles, setDroppedFiles, setLastWarnings, setIsGenerating, collapsed }) {
    const handleGenerate = async () => {
        const pending = droppedFiles.filter(function isPending(entry) { return entry.status === 'pending'; });
        if (pending.length === 0) {
            return;
        }
        setIsGenerating(true);

        const warnings = [];
        const parsedIds = [];

        for (const entry of pending) {
            const strategy = getImportStrategy(entry.strategyKey);
            if (!strategy?.run) continue;
            const source = await entry.file.text();
            const { x, y } = nextPosition();
            const result = strategy.run(source, x, y, umlCanvasEngine.shapes, umlConnectorEngine.connectors, { collapsed });
            const shapes = result?.shapes ?? [];
            const connectors = result?.connectors ?? [];
            shapes.forEach(function addEachShape(shape) { umlCanvasEngine.addShape(shape); });
            connectors.forEach(function addEachConnector(connector) { umlConnectorEngine.addConnector(connector); });
            (result?.warnings ?? []).forEach(function toWarningText(w) { warnings.push(`${entry.name}: ${w}`); });
            parsedIds.push(entry.id);
        }

        setDroppedFiles(function markParsed(current) {
            return current.map(function updateEntry(entry) {
                return parsedIds.includes(entry.id) ? { ...entry, status: 'parsed' } : entry;
            });
        });

        // Retroactive full-canvas pass (same as the Import tab's "Find Connectors"
        // button) — cross-file inheritance/associations that the per-file parse
        // above didn't already catch, now that every file's shapes are on canvas.
        const foundConnectors = findCanvasConnectors(umlCanvasEngine.shapes, umlConnectorEngine.connectors);
        foundConnectors.forEach(function addEachConnector(connector) { umlConnectorEngine.addConnector(connector); });

        setLastWarnings(warnings);
        setIsGenerating(false);
    };

    return { handleGenerate };
}
